import React, { lazy, Suspense, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'Components';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import Heading from 'LearningManager/components/Heading';
// import ReportActions from 'LearningManager/components/ReportActions';
import styles from 'LearningManager/styles/TrainingReports.module.scss';
import ReportFilters from './components/ReportFilters';
import ViewToggle from './components/ViewToggle';
import useReportFilterOptions from './hooks/useReportFilterOptions';
import useReportTenantId from './hooks/useReportTenantId';
import ReportTabs from './Table/ReportTabs';
import {
	DEFAULT_TABLE_TAB,
	REPORT_TABS
} from './Table/TrainingTable.constants';
import { ReportFiltersProvider } from './context/ReportFilters.context';
import { ReportTableTab, ReportView } from './TrainingReports.types';
import {
	DEFAULT_REPORT_VIEW,
	SEARCH_PLACEHOLDER
} from './TrainingReports.constants';

const Dashboard = lazy(() => import('./Dashboard/Dashboard'));
const TrainingTable = lazy(() => import('./Table/TrainingTable'));

const TrainingReports = observer(() => {
	const [view, setView] = useState<ReportView>(DEFAULT_REPORT_VIEW);
	const [tableTab, setTableTab] = useState<ReportTableTab>(DEFAULT_TABLE_TAB);
	const { scopeTenantId } = useReportTenantId();
	const { t } = useTranslation();

	const isTableView = view === ReportView.Report;

	const searchPlaceholder = isTableView
		? REPORT_TABS.find((tab) => tab.key === tableTab)?.searchPlaceholder ??	SEARCH_PLACEHOLDER
		: SEARCH_PLACEHOLDER;

	return (
		<ReportFiltersProvider key={scopeTenantId ?? 'none'}>
			<div className={styles.page}>
				<TopSection view={view} onViewChange={setView} t={t} />

				{isTableView && (
					<ReportTabs value={tableTab} onChange={setTableTab} />
				)}

				<FilterBar searchPlaceholder={t(searchPlaceholder)} />

				<Suspense fallback={<ViewFallback />}>
					{isTableView ? (
						<TrainingTable tab={tableTab} />
					) : (
						<Dashboard />
					)}
				</Suspense>
			</div>
		</ReportFiltersProvider>
	);
});

interface FilterBarProps {
	searchPlaceholder: string;
}

const FilterBar = observer(({ searchPlaceholder }: FilterBarProps) => {
	const { tenantId, scopeTenantId } = useReportTenantId();
	const options = useReportFilterOptions(tenantId, scopeTenantId);

	return (
		<div className={styles.filterBar}>
			<ReportFilters
				className={styles.filters}
				options={options}
				searchPlaceholder={searchPlaceholder}
			/>
			{/* <ReportActions className={styles.filterActions} /> */}
		</div>
	);
});

interface TopSectionProps {
	view: ReportView;
	onViewChange: (view: ReportView) => void;
	t: TFunction
}

const TopSection: React.FC<TopSectionProps> = ({ view, onViewChange, t }) => {
	return (
		<div className={styles.heading}>
			<Heading
				titleClassName={styles.title}
				title={t(`learning.report.${view === 'report' ? 'userTitle' : 'title'}`)}
				subTitleClassName={styles.subtitle}
				subHeading={t('learning.report.subTitle')}
			/>
			<ViewToggle value={view} onChange={onViewChange} />
		</div>
	);
};

const ViewFallback = () => (
	<div className={styles.viewFallback}>
		<Spin size="large" />
	</div>
);

export default TrainingReports;
